/**
 * Custom Hook for Email Logs Management
 * Manages state and operations for email logs (Journaux d'emails)
 * Migrated to TanStack Query for automatic caching and refetching
 */

import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  emailLogsAPI,
  EmailLog,
  EmailStatistics,
  GetEmailLogsParams,
  EmailStatus,
} from '../utils/emailLogsAPI';
import { useAuth } from './useAuth';

export interface UseEmailLogsOptions {
  autoLoad?: boolean;
}

export interface UseEmailLogsReturn {
  // State
  logs: EmailLog[];
  statistics: EmailStatistics | null;
  loading: boolean;
  error: string | null;

  // Actions
  loadLogs: (params?: GetEmailLogsParams) => Promise<void>;
  loadStatistics: (startDate?: string, endDate?: string) => Promise<void>;
  loadHistory: (entityType: string, entityId: string) => Promise<EmailLog[]>;
  sendTestEmail: () => Promise<void>;
  getStatus: () => Promise<{ provider: string; configured: boolean }>;
  clearError: () => void;
}

// Query keys
const emailLogKeys = {
  all: ['email-logs'] as const,
  logs: (tenantId?: string, companyId?: string, params?: GetEmailLogsParams) =>
    [...emailLogKeys.all, 'list', tenantId, companyId, params] as const,
  statistics: (tenantId?: string, companyId?: string, startDate?: string, endDate?: string) =>
    [...emailLogKeys.all, 'stats', tenantId, companyId, startDate, endDate] as const,
};

/**
 * Hook for managing email logs
 */
export function useEmailLogs(options?: UseEmailLogsOptions): UseEmailLogsReturn {
  const { autoLoad = false } = options || {};
  const { user } = useAuth();
  const tenantId = user?.tenantId;
  const companyId = user?.companyId;
  const queryClient = useQueryClient();

  // Local state for dynamic filter params (loadLogs can be called with different params)
  const [logsParams, setLogsParams] = useState<GetEmailLogsParams | undefined>(undefined);
  const [statsDateRange, setStatsDateRange] = useState<{ startDate?: string; endDate?: string }>({});
  const [manualError, setManualError] = useState<string | null>(null);

  /**
   * Query: email logs
   */
  const {
    data: logs = [],
    isLoading: logsLoading,
    error: logsError,
  } = useQuery<EmailLog[], Error>({
    queryKey: emailLogKeys.logs(tenantId, companyId, logsParams),
    queryFn: async () => {
      const { logs: data } = await emailLogsAPI.getLogs(logsParams);
      return data;
    },
    enabled: autoLoad && !!tenantId,
    staleTime: 30 * 1000, // 30 seconds
  });

  /**
   * Query: email statistics
   */
  const {
    data: statistics = null,
    isLoading: statsLoading,
    error: statsError,
  } = useQuery<EmailStatistics | null, Error>({
    queryKey: emailLogKeys.statistics(tenantId, companyId, statsDateRange.startDate, statsDateRange.endDate),
    queryFn: async () => {
      const data = await emailLogsAPI.getStatistics(statsDateRange.startDate, statsDateRange.endDate);
      return data;
    },
    enabled: autoLoad && !!tenantId,
    staleTime: 60 * 1000, // 1 minute
  });

  /**
   * Mutation: send test email
   */
  const sendTestMutation = useMutation({
    mutationFn: () => emailLogsAPI.sendTestEmail(),
    onSuccess: () => {
      // Invalidate logs after sending test email
      queryClient.invalidateQueries({ queryKey: emailLogKeys.all });
    },
    onError: (err: any) => {
      const errorMessage =
        err.response?.data?.message || err.message || 'Erreur lors de l\'envoi de l\'email de test';
      setManualError(errorMessage);
    },
  });

  /**
   * Load email logs with optional filters
   */
  const loadLogs = useCallback(async (params?: GetEmailLogsParams) => {
    setManualError(null);
    setLogsParams(params);
    // Trigger refetch by invalidating with new params
    await queryClient.invalidateQueries({
      queryKey: emailLogKeys.logs(tenantId, companyId, params),
    });
    // Also ensure the query runs if it wasn't enabled before
    await queryClient.fetchQuery({
      queryKey: emailLogKeys.logs(tenantId, companyId, params),
      queryFn: async () => {
        const { logs: data } = await emailLogsAPI.getLogs(params);
        return data;
      },
    });
  }, [tenantId, companyId, queryClient]);

  /**
   * Load email statistics
   */
  const loadStatistics = useCallback(async (startDate?: string, endDate?: string) => {
    setManualError(null);
    setStatsDateRange({ startDate, endDate });
    await queryClient.fetchQuery({
      queryKey: emailLogKeys.statistics(tenantId, companyId, startDate, endDate),
      queryFn: async () => {
        const data = await emailLogsAPI.getStatistics(startDate, endDate);
        return data;
      },
    });
  }, [tenantId, companyId, queryClient]);

  /**
   * Load email history for a specific entity
   */
  const loadHistory = useCallback(
    async (entityType: string, entityId: string): Promise<EmailLog[]> => {
      try {
        setManualError(null);
        const data = await emailLogsAPI.getHistory(entityType, entityId);
        return data;
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.message || err.message || 'Erreur lors du chargement de l\'historique';
        setManualError(errorMessage);
        throw new Error(errorMessage);
      }
    },
    []
  );

  /**
   * Send a test email
   */
  const sendTestEmail = useCallback(async (): Promise<void> => {
    await sendTestMutation.mutateAsync();
  }, [sendTestMutation]);

  /**
   * Get email service status
   */
  const getStatus = useCallback(async (): Promise<{ provider: string; configured: boolean }> => {
    try {
      setManualError(null);
      const status = await emailLogsAPI.getStatus();
      return status;
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.message || 'Erreur lors de la vérification du statut';
      setManualError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setManualError(null);
  }, []);

  // Combine loading states
  const loading = logsLoading || statsLoading || sendTestMutation.isPending;

  // Combine error states: manual error takes precedence, then query errors
  const error = manualError ||
    (logsError ? (logsError instanceof Error ? logsError.message : 'Erreur lors du chargement des emails') : null) ||
    (statsError ? (statsError instanceof Error ? statsError.message : 'Erreur lors du chargement des statistiques') : null);

  return {
    // State
    logs,
    statistics,
    loading,
    error,

    // Actions
    loadLogs,
    loadStatistics,
    loadHistory,
    sendTestEmail,
    getStatus,
    clearError,
  };
}
